/**
 * Functions, not constants: a warm server instance evaluates this module once and would keep
 * serving the day it booted on, while the browser re-evaluates it on every page load. Server and
 * client then render different days and hydration of the program page fails (CIN-14426).
 */
export function dateToday(): Date {
    return new Date();
}

export function logicalStartOfToday(): Date {
    return getLogicalStartOfDay(dateToday());
}

export const dateTomorrow = () => {
    const date = dateToday();
    date.setDate(date.getDate() + 1);
    return date;
};

const MILLISECONDS_IN_DAY = 1000 * 60 * 60 * 24;

export function dateTodayMinus(days = 0, hours = 0, minutes = 0, seconds = 0) {
    const date = logicalStartOfToday();
    if (days !== 0) {
        date.setDate(date.getDate() - days);
    }
    if (hours !== 0 || minutes !== 0 || seconds !== 0) {
        date.setTime(date.getTime() - (1000 * seconds + 1000 * 60 * minutes + 1000 * 60 * 60 * hours));
    }
    return date;
}

export function dateTodayPlus(days = 0, hours = 0, minutes = 0, seconds = 0) {
    const date = logicalStartOfToday();
    if (days !== 0) {
        date.setDate(date.getDate() + days);
    }
    if (hours !== 0 || minutes !== 0 || seconds !== 0) {
        date.setTime(date.getTime() + (1000 * seconds + 1000 * 60 * minutes + 1000 * 60 * 60 * hours));
    }
    return date;
}

export function dateMinus(date: Date, days = 0, hours = 0, minutes = 0, seconds = 0) {
    const newDate = new Date(date);
    if (days !== 0) {
        newDate.setDate(newDate.getDate() - days);
    }
    if (hours !== 0 || minutes !== 0 || seconds !== 0) {
        newDate.setTime(newDate.getTime() - (1000 * seconds + 1000 * 60 * minutes + 1000 * 60 * 60 * hours));
    }
    return newDate;
}

export type DateTimeFormatOptions = Intl.DateTimeFormatOptions;

export function formatDate(
    date: string | Date,
    locales: string = 'de-DE',
    options: DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    }
): string {
    const tmpDate: Date = new Date(date);
    if (tmpDate) {
        return new Intl.DateTimeFormat(locales, options).format(tmpDate);
    }
    return null;
}

export function formatDay(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                weekday: 'short'
            }
        );
    }
    return null;
}

export function formatDayNumeric(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                day: 'numeric'
            }
        );
    }
    return null;
}

export function formatMonthLong(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                month: 'long'
            }
        );
    }
    return null;
}

export function formatYearNumeric(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    if (date) {
        return formatDate(
            date,
            locales || 'de-DE',
            options || {
                year: 'numeric'
            }
        );
    }
    return null;
}

export function formatTime(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    return formatDate(date, locales, {
        hour: 'numeric',
        minute: 'numeric',
        ...options
    });
}

export function formatDateTime(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    return formatDate(
        date,
        locales,
        options || {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }
    );
}

export function formatDateNumbersOnly(date: string | Date, locales?: string, options?: DateTimeFormatOptions): string {
    return formatDate(
        date,
        locales,
        options || {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }
    );
}

export function formatDateHyphenSeparatedYearMonthDay(date: string | Date): string {
    return formatDate_yyyyMMdd(date);
}

export function formatDate_HHmm24(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

export function formatDateForMyRequestsList(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).replace('.,', ',');
}

export function formatDayMonth(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        day: '2-digit',
        month: '2-digit'
    });
}

export function formatDateWeekdayYearMonthDay(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).replace('.,', ',');
}

export function formatDateWeekdayYearMonthDayHourMinute(date: string | Date, locales?: string): string {
    return formatDate(date, locales, {
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).replace('.,', ',');
}

export function getAge(dateString: string | Date): number {
    const birthDate = new Date(dateString);
    const today = dateToday();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
}

export function createServerDate(value: string | Date) {
    const valueAsDateString = new Date(value);
    valueAsDateString.setUTCHours(0, 0, 0, 0);
    const isoString = valueAsDateString.toISOString();
    return isoString.replace('.000Z', 'Z');
}

export function formatDate_yyyyMMdd(value: Date | string): string {
    const date = new Date(value);
    if (isNaN(Number(date))) {
        return null;
    }
    const localDate = new Date(date.getTime());
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function formatDate_yyyyMMddTHHmmssZ(value: Date): string {
    const isoString = value.toISOString();
    return isoString.substring(0, 19) + 'Z';
}

export const isDateFuture = (date: string | Date): boolean => {
    if (!date) {
        return false;
    }
    return new Date(date) > dateToday();
};

export const isDateToday = (date: string | Date): boolean => {
    if (!date) {
        return false;
    }
    const dateAsDate = new Date(date);
    const today = dateToday();

    return (
        dateAsDate.getFullYear() === today.getFullYear() &&
        dateAsDate.getMonth() === today.getMonth() &&
        dateAsDate.getDate() === today.getDate()
    );
};

/* The logical day for a cinema starts at 4am and ends the next day at 3:59am!  */
export function getLogicalStartOfDay(date: Date | string): Date {
    const logicalDatetime: Date = new Date(date);
    logicalDatetime.setHours(4, 0, 0, 0);
    return logicalDatetime;
}
export function getLogicalEndOfDay(date: Date | string): Date {
    const logicalDatetime: Date = new Date(date);
    logicalDatetime.setHours(3, 59, 0, 0);
    logicalDatetime.setDate(logicalDatetime.getDate() + 1);
    return logicalDatetime;
}

export function getWeekNumber(date: Date): number {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}

export function getWeeksBetweenDates(
    startDate: Date,
    endDate: Date
): { weekNumber: number; startDate: Date; endDate: Date }[] {
    const weeks: { weekNumber: number; startDate: Date; endDate: Date }[] = [];

    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
        const weekStart = new Date(currentDate);
        const weekNumber = getWeekNumber(currentDate);
        currentDate.setDate(currentDate.getDate() + 6); // move to the end of the week
        const weekEnd = currentDate > endDate ? new Date(endDate) : new Date(currentDate);

        weeks.push({
            weekNumber,
            startDate: weekStart,
            endDate: weekEnd
        });

        currentDate.setDate(currentDate.getDate() + 1); // move to the next week's start
    }

    return weeks;
}

export function getDateFourWeeksAgo() {
    const today = new Date();
    today.setDate(today.getDate() - 28);
    return today;
}

export function getDates(visibleDaysInFuture = 14): Date[] {
    return [...Array(visibleDaysInFuture)].map((_, index) => {
        const date = new Date();
        date.setDate(date.getDate() + index);
        return date;
    });
}

/**
 * Whether a yyyy-MM-dd date is one of the days the calendar bar renders.
 * Dates beyond that window are only reachable through the date picker, so the
 * bar cannot show which day is selected and the showings have to say it themselves.
 *
 * Deliberately derived from `getDates()` rather than from day arithmetic: it keeps the
 * window defined in exactly one place (the bar renders the same array) and inherits its
 * DST-safe `setDate()` stepping. The cost is one 14-element array per call, which is
 * meant to be paid once per component render — do not call it per list item.
 */
export function isDateWithinCalendarBar(date: string, visibleDaysInFuture?: number): boolean {
    return getDates(visibleDaysInFuture).some((visibleDate) => formatDate_yyyyMMdd(visibleDate) === date);
}

export function dateAsStringOrNull(date: string | Date): string {
    return date ? formatDate_yyyyMMddTHHmmssZ(new Date(date)) : null;
}

export function daysBetween(date1: Date, date2: Date) {
    return Math.floor(Math.abs((date1.getTime() - date2.getTime()) / MILLISECONDS_IN_DAY));
}

export const getDateAtHour = (dateStr: string | Date, hour: number) => {
    const date = new Date(dateStr);
    date.setHours(hour, 0, 0, 0);
    return date;
};

export const getDateInTimezone = (date: string | Date, timezone: string = 'Europe/Berlin') => {
    return new Date(new Date(date).toLocaleString('en-US', { timeZone: timezone }));
};
